/**
 * KB Completeness Dashboard Component
 *
 * Displays comprehensive knowledge base status including:
 * - Overall completeness score
 * - Per-section breakdown
 * - Data freshness indicators
 * - Source quality distribution
 * - Missing data gaps
 */

import { useMemo } from 'react';
import type { KBStatus, KBSectionFreshness } from '../../types';

interface KBDashboardProps {
  kbStatus: KBStatus;
  companyName: string;
}

// Freshness configuration
const FRESHNESS_CONFIG: Record<KBSectionFreshness, { label: string; color: string; bgColor: string; icon: string }> = {
  current: { label: 'Current', color: 'text-green-700', bgColor: 'bg-green-100', icon: 'check' },
  stale: { label: 'Stale', color: 'text-yellow-700', bgColor: 'bg-yellow-100', icon: 'clock' },
  outdated: { label: 'Outdated', color: 'text-red-700', bgColor: 'bg-red-100', icon: 'alert' },
};

// Section weights for display
const SECTION_WEIGHTS: Record<string, number> = {
  fundamentals: 15,
  key_metrics: 25,
  stock_performance: 10,
  leadership: 15,
  products: 20,
  news: 15,
};

function ProgressRing({ percentage, size = 120 }: { percentage: number; size?: number }) {
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  // Color based on percentage
  const getColor = (pct: number) => {
    if (pct >= 80) return '#10B981'; // green
    if (pct >= 60) return '#F59E0B'; // yellow
    if (pct >= 40) return '#F97316'; // orange
    return '#EF4444'; // red
  };

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#E5E7EB"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={getColor(percentage)}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-500"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-2xl font-bold text-gray-900">{Math.round(percentage)}%</span>
      </div>
    </div>
  );
}

function SectionBar({
  name,
  completeness,
  freshness,
  weight,
}: {
  name: string;
  completeness: number;
  freshness: KBSectionFreshness;
  weight: number;
}) {
  const freshnessConfig = FRESHNESS_CONFIG[freshness];
  const percentage = completeness * 100;

  // Format section name
  const formatName = (n: string) => {
    return n
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">{formatName(name)}</span>
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${freshnessConfig.bgColor} ${freshnessConfig.color}`}>
            {freshnessConfig.label}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">{weight}% weight</span>
          <span className="text-sm font-medium text-gray-900">{Math.round(percentage)}%</span>
        </div>
      </div>
      <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${
            freshness === 'current' ? 'bg-green-500' :
            freshness === 'stale' ? 'bg-yellow-500' : 'bg-red-500'
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

function FreshnessLegend() {
  return (
    <div className="flex items-center gap-4 text-xs">
      {Object.entries(FRESHNESS_CONFIG).map(([key, config]) => (
        <div key={key} className="flex items-center gap-1">
          <div className={`w-2 h-2 rounded-full ${config.bgColor}`} />
          <span className="text-gray-600">{config.label}</span>
        </div>
      ))}
    </div>
  );
}

function SourceQualityChart({ sections }: { sections: Record<string, { source_count: number }> }) {
  // Calculate total sources and tier distribution (memoized)
  const { totalSources, tierDistribution } = useMemo(() => {
    const total = Object.values(sections).reduce((sum, s) => sum + s.source_count, 0);
    // Mock tier distribution (would come from real data)
    const distribution = [
      { tier: 1, label: 'Authoritative', count: Math.round(total * 0.3), color: 'bg-green-500' },
      { tier: 2, label: 'High Quality', count: Math.round(total * 0.4), color: 'bg-blue-500' },
      { tier: 3, label: 'Moderate', count: Math.round(total * 0.2), color: 'bg-yellow-500' },
      { tier: 4, label: 'Signal', count: Math.round(total * 0.1), color: 'bg-gray-400' },
    ];
    return { totalSources: total, tierDistribution: distribution };
  }, [sections]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">Source Quality</span>
        <span className="text-xs text-gray-500">{totalSources} total sources</span>
      </div>
      <div className="flex h-4 rounded-full overflow-hidden">
        {tierDistribution.map((tier) => (
          <div
            key={tier.tier}
            className={`${tier.color} transition-all`}
            style={{ width: `${(tier.count / totalSources) * 100}%` }}
            title={`${tier.label}: ${tier.count} sources`}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
        {tierDistribution.map((tier) => (
          <div key={tier.tier} className="flex items-center gap-1">
            <div className={`w-2 h-2 rounded-full ${tier.color}`} />
            <span className="text-gray-600">{tier.label}</span>
            <span className="text-gray-400">({tier.count})</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MissingDataList({ sections }: { sections: Record<string, { completeness: number }> }) {
  // Find sections with gaps (memoized to avoid recalculating on each render)
  const gaps = useMemo(
    () =>
      Object.entries(sections)
        .filter(([, s]) => s.completeness < 1)
        .sort((a, b) => a[1].completeness - b[1].completeness)
        .slice(0, 5),
    [sections]
  );

  if (gaps.length === 0) {
    return (
      <div className="text-sm text-green-600 flex items-center gap-2">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
        All sections complete
      </div>
    );
  }

  const formatName = (n: string) => {
    return n.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div className="space-y-2">
      <span className="text-sm font-medium text-gray-700">Data Gaps</span>
      <ul className="space-y-1">
        {gaps.map(([name, section]) => (
          <li key={name} className="flex items-center justify-between text-sm">
            <span className="text-gray-600">{formatName(name)}</span>
            <span className="text-red-600 font-medium">
              {Math.round((1 - section.completeness) * 100)}% missing
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function KBDashboard({ kbStatus, companyName }: KBDashboardProps) {
  const { overall_completeness, sections, last_full_refresh, is_building } = kbStatus;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Knowledge Base Status</h2>
          <p className="text-sm text-gray-500">{companyName}</p>
        </div>
        {is_building && (
          <div className="flex items-center gap-2 text-sm text-primary-600">
            <div className="w-2 h-2 bg-primary-600 rounded-full animate-pulse" />
            Building...
          </div>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Overall Score */}
        <div className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-lg">
          <ProgressRing percentage={overall_completeness * 100} />
          <p className="mt-2 text-sm font-medium text-gray-700">Overall Completeness</p>
          {last_full_refresh && (
            <p className="text-xs text-gray-500 mt-1">
              Last refresh: {new Date(last_full_refresh).toLocaleDateString()}
            </p>
          )}
        </div>

        {/* Section Breakdown */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Section Breakdown</span>
            <FreshnessLegend />
          </div>
          <div className="space-y-3">
            {Object.entries(sections).map(([name, section]) => (
              <SectionBar
                key={name}
                name={name}
                completeness={section.completeness}
                freshness={section.freshness}
                weight={SECTION_WEIGHTS[name] || 10}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Bottom row */}
      <div className="grid gap-6 lg:grid-cols-2 mt-6 pt-6 border-t border-gray-200">
        <SourceQualityChart sections={sections} />
        <MissingDataList sections={sections} />
      </div>
    </div>
  );
}

export default KBDashboard;
